/**
 * GeoJSON Layer Helper
 * Provides a deterministic, idempotent helper for adding/updating GeoJSON-based map layers.
 * Safe to call multiple times; will update existing sources in-place or recreate atomically.
 */
import type { AnyLayer, GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';

/**
 * Wait for map style to be loaded with timeout
 * @returns true if style is loaded, false if timed out
 */
export async function waitForStyleLoaded(map: MapboxMap | null, timeoutMs = 10000): Promise<boolean> {
  if (!map) return false;
  if (map.isStyleLoaded()) return true;

  return new Promise((resolve) => {
    let resolved = false;
    const timeoutId = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      console.warn('waitForStyleLoaded: timed out');
      resolve(false);
    }, timeoutMs);

    const checkStyle = () => {
      if (resolved) return;
      if (map.isStyleLoaded()) {
        resolved = true;
        clearTimeout(timeoutId);
        resolve(true);
      }
    };

    // Check on idle events
    map.on('idle', checkStyle);

    // Also poll occasionally as backup
    const pollId = setInterval(() => {
      if (resolved) {
        clearInterval(pollId);
        return;
      }
      checkStyle();
    }, 100);

    // Cleanup after timeout
    setTimeout(() => {
      clearInterval(pollId);
      map.off('idle', checkStyle);
    }, timeoutMs + 100);
  });
}

export interface GeoJsonLayerOptions {
  /** Mapbox map instance */
  map: MapboxMap;
  /** Unique source ID */
  sourceId: string;
  /** Unique layer ID */
  layerId: string;
  /** GeoJSON data to render (FeatureCollection or similar) */
  data: GeoJSON.FeatureCollection | GeoJSON.Feature | null;
  /** Mapbox layer configuration (type, paint, layout, etc.) – must NOT include `id` or `source` */
  layerConfig: Omit<AnyLayer, 'id' | 'source'>;
  /** Optional: layer to insert before (for ordering) */
  beforeLayerId?: string;
}

/**
 * Ensure a GeoJSON layer exists and is up-to-date.
 * - If the source exists and layer type matches, updates data in-place.
 * - If the layer type changed (e.g. circle -> symbol), recreates the layer.
 * - If the source/layer are missing, creates them.
 * - If data is null or an empty FeatureCollection, clears the source (removes markers).
 *
 * @returns true if the layer is now present; false if map/style not ready.
 */
export function ensureGeoJsonLayer(options: GeoJsonLayerOptions): boolean {
  const { map, sourceId, layerId, data, layerConfig, beforeLayerId } = options;

  // Guard: map must exist
  if (!map || typeof map.isStyleLoaded !== 'function') {
    console.warn(`ensureGeoJsonLayer [${layerId}]: map not available`);
    return false;
  }

  // Check if style is loaded - if not, log but don't fail silently
  if (!map.isStyleLoaded()) {
    console.warn(`ensureGeoJsonLayer [${layerId}]: style not loaded, skipping`);
    return false;
  }

  // Normalize empty data
  const normalizedData: GeoJSON.FeatureCollection =
    data && 'features' in data && Array.isArray(data.features)
      ? (data as GeoJSON.FeatureCollection)
      : { type: 'FeatureCollection', features: [] };

  // Get the desired layer type from config
  const desiredType = layerConfig.type as string;

  // Check if existing layer has a different type (e.g. circle vs symbol)
  const existingLayer = map.getLayer(layerId);
  const existingLayerType = existingLayer ? existingLayer.type : null;
  const layerTypeMismatch = existingLayer && existingLayerType !== desiredType;

  // If layer type changed, we need to recreate the layer
  if (layerTypeMismatch) {
    // eslint-disable-next-line no-console
    // Layer type changed; rebuild without logging to console.
    removeLayerSafe(map, layerId);
    removeSourceSafe(map, sourceId);
  }

  // If source exists (and layer type matches), update in-place
  const existingSource = map.getSource(sourceId) as GeoJSONSource | undefined;
  if (existingSource && typeof existingSource.setData === 'function' && !layerTypeMismatch) {
    existingSource.setData(normalizedData);
    // Ensure layer exists (might have been removed by style reload)
    if (!map.getLayer(layerId)) {
      addLayerSafe(map, layerId, sourceId, layerConfig, beforeLayerId);
    }
    map.triggerRepaint();
    return true;
  }

  // Source doesn't exist – remove stale layer if present, then create fresh
  removeLayerSafe(map, layerId);
  removeSourceSafe(map, sourceId);

  try {
    map.addSource(sourceId, {
      type: 'geojson',
      data: normalizedData,
    });
  } catch (err) {
    console.error(`ensureGeoJsonLayer: failed to add source ${sourceId}`, err);
    return false;
  }

  return addLayerSafe(map, layerId, sourceId, layerConfig, beforeLayerId);
}

/**
 * Remove a GeoJSON layer and its source if they exist.
 */
export function removeGeoJsonLayer(map: MapboxMap | null, layerId: string, sourceId: string): void {
  if (!map || !map.isStyleLoaded()) return;
  removeLayerSafe(map, layerId);
  removeSourceSafe(map, sourceId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function removeLayerSafe(map: MapboxMap, layerId: string): void {
  try {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  } catch {
    // ignore
  }
}

function removeSourceSafe(map: MapboxMap, sourceId: string): void {
  try {
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  } catch {
    // ignore
  }
}

function addLayerSafe(
  map: MapboxMap,
  layerId: string,
  sourceId: string,
  layerConfig: Omit<AnyLayer, 'id' | 'source'>,
  beforeLayerId?: string,
): boolean {
  try {
    const fullConfig = {
      ...layerConfig,
      id: layerId,
      source: sourceId,
    } as AnyLayer;

    map.addLayer(fullConfig, beforeLayerId);
    return true;
  } catch (err) {
    console.error(`ensureGeoJsonLayer: failed to add layer ${layerId}`, err);
    return false;
  }
}
